import { useState, useEffect } from 'react';
import { FileSearch, FileText, Shield } from 'lucide-react';

const ProcessSlider = () => {
  const [currentStep, setCurrentStep] = useState(0);

  const steps = [
    {
      icon: FileSearch,
      title: 'Search & Analysis',
      description: 'We conduct a comprehensive trademark search to ensure your brand is unique and available for registration.'
    },
    {
      icon: FileText,
      title: 'Application Filing',
      description: 'Our experts prepare and file your trademark application with meticulous attention to detail, maximizing approval chances.'
    },
    {
      icon: Shield,
      title: 'Protection & Monitoring',
      description: 'Once approved, we help monitor your trademark and provide ongoing support to protect your intellectual property.'
    }
  ];

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentStep((prev) => (prev + 1) % steps.length);
    }, 4000);
    return () => clearInterval(timer);
  }, [steps.length]);

  return (
    <section className="py-20 bg-gray-50" data-testid="process-section">
      <div className="container mx-auto px-4">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold mb-4">Simple 3-Step Process</h2>
          <p className="text-xl text-gray-600">Protecting your brand has never been easier</p>
        </div>

        {/* Desktop View */}
        <div className="hidden md:grid md:grid-cols-3 gap-8">
          {steps.map((step, idx) => {
            const Icon = step.icon;
            return (
              <div
                key={idx}
                className={`bg-white rounded-2xl p-8 text-center card-hover transition-all duration-500 ${
                  currentStep === idx ? 'ring-4 ring-red-500 transform scale-105' : ''
                }`}
                data-testid={`process-step-${idx}`}
              >
                <div className="flex justify-center mb-6">
                  <div className="w-20 h-20 bg-gradient-to-br from-red-600 to-orange-500 rounded-full flex items-center justify-center">
                    <Icon className="w-10 h-10 text-white" />
                  </div>
                </div>
                <div className="mb-4">
                  <span className="inline-block bg-red-100 text-red-600 px-4 py-1 rounded-full text-sm font-semibold mb-4">
                    Step {idx + 1}
                  </span>
                </div>
                <h3 className="text-2xl font-bold mb-4">{step.title}</h3>
                <p className="text-gray-600 leading-relaxed">{step.description}</p>
              </div>
            );
          })}
        </div>

        {/* Mobile View - Slider */}
        <div className="md:hidden">
          <div className="bg-white rounded-2xl p-8 text-center shadow-lg">
            {(() => {
              const Icon = steps[currentStep].icon;
              return (
                <>
                  <div className="flex justify-center mb-6">
                    <div className="w-20 h-20 bg-gradient-to-br from-red-600 to-orange-500 rounded-full flex items-center justify-center">
                      <Icon className="w-10 h-10 text-white" />
                    </div>
                  </div>
                  <div className="mb-4">
                    <span className="inline-block bg-red-100 text-red-600 px-4 py-1 rounded-full text-sm font-semibold mb-4">
                      Step {currentStep + 1}
                    </span>
                  </div>
                  <h3 className="text-2xl font-bold mb-4">{steps[currentStep].title}</h3>
                  <p className="text-gray-600 leading-relaxed">{steps[currentStep].description}</p>
                </>
              );
            })()}
          </div>

          <div className="flex justify-center mt-8 space-x-2">
            {steps.map((_, idx) => (
              <button
                key={idx}
                onClick={() => setCurrentStep(idx)}
                className={`w-3 h-3 rounded-full transition-all ${
                  currentStep === idx ? 'bg-red-600 w-8' : 'bg-gray-300'
                }`}
                data-testid={`process-dot-${idx}`}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default ProcessSlider;